import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHeader } from '@/components/landing/page-header'

const SECTIONS: { title: string, paragraphs: string[] }[] = [
    {
        title: "Who we are",
        paragraphs: [
            "This website is run by Roamwood Tiny Homes. This notice explains what personal data the site collects, why, and what you can ask us to do with it."
        ]
    },
    {
        title: "What we collect",
        paragraphs: [
            "When you send a quote request, we receive the details you enter: your name, email address, phone number if you give one, the model you are interested in, how you plan to use your home, your timeline, whether you have land, and your message."
        ]
    },
    {
        title: "How we use it",
        paragraphs: [
            "We use your details only to answer your request, prepare a quote and follow up on your project. We never sell your details and never share them with anyone for marketing."
        ]
    },
    {
        title: "Where it is stored",
        paragraphs: [
            "Requests are stored securely in our website database and sent to our team by email so we can reply quickly.",
            "These notification emails go through our website platform, which keeps basic delivery information and records whether an email was opened or a link in it was clicked."
        ]
    },
    {
        title: "Visitor statistics and cookies",
        paragraphs: [
            "We count visits to this website with privacy-friendly statistics that do not use cookies and do not identify you personally. This website does not use advertising or tracking cookies."
        ]
    },
    {
        title: "How long we keep it",
        paragraphs: [
            "We keep quote requests for as long as we need them to handle your project and delete them when they are no longer needed."
        ]
    },
    {
        title: "Your rights",
        paragraphs: [
            "You can ask to see, correct or delete the personal data we hold about you at any time, or object to how we use it. Send us a message through our contact form and we will take care of it."
        ]
    }
]

const PrivacyPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Privacy notice"
                description="How Roamwood Tiny Homes collects, uses and protects the personal data you share through this website."
            />

            <PageHeader
                eyebrow="Privacy"
                title="Privacy notice"
                description="Plain language about what happens to the information you share with us."
            />

            <section id='privacy-notice' className='py-16 lg:py-24'>
                <div className='mx-auto max-w-3xl px-5 sm:px-8'>
                    <p className='text-sm text-muted-foreground'>
                        Last updated: November 2026
                    </p>

                    <div className='mt-10 space-y-12'>
                        {SECTIONS.map(section => (
                            <div key={section.title}>
                                <h2 className='font-heading text-2xl font-medium tracking-tight sm:text-3xl'>
                                    {section.title}
                                </h2>

                                <div className='mt-4 space-y-4 text-lg leading-relaxed text-muted-foreground'>
                                    {section.paragraphs.map(paragraph => (
                                        <p key={paragraph}>
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='mt-14 rounded-2xl border border-border bg-card p-7'>
                        <p className='leading-relaxed'>
                            Questions about your data?{' '}
                            <Link href='/contact' className='font-semibold text-primary underline underline-offset-4'>
                                Send us a message
                            </Link>
                            {' '}and we will reply personally.
                        </p>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default PrivacyPage
